/* eslint-disable import/no-default-export, import/order, max-lines, no-nested-ternary, react/no-unescaped-entities, react/prop-types */
import { useState, useEffect } from "react";
import { submitFeedback } from "./usePinningSession";

const SUCCESS_COLLAPSE_DELAY_MS = 1500;

/**
 * Hook for managing pinning session feedback (thumbs up/down).
 *
 * @param {string | null} sessionId - The current session ID
 * @param {Array} pins - The pins array
 * @param {string | null} lastEditedPinId - The last edited pin ID
 * @returns {object} Feedback state and handlers
 */
// eslint-disable-next-line import/prefer-default-export, max-lines-per-function, max-params
export function usePinningFeedback(
  sessionId: any,
  pins: any,
  lastEditedPinId: any,
  { toast }: any = {}
) {
  const [positive, setPositive] = useState(null);
  const [message, setMessage] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState("idle");

  function lastEditedPin() {
    if (!lastEditedPinId) return null;
    return pins?.find((pin: any) => pin.id === lastEditedPinId) ?? null;
  }

  function resetFeedback() {
    setPositive(null);
    setMessage("");
    setIsOpen(false);
    setStatus("idle");
  }

  function handleSelectPositive() {
    // @ts-expect-error TS(2345): Argument of type 'true' is not assignable to param... Remove this comment to see the full error message
    setPositive(true);
    setIsOpen(true);
    setStatus("idle");
  }

  function handleSelectNegative() {
    // @ts-expect-error TS(2345): Argument of type 'false' is not assignable to para... Remove this comment to see the full error message
    setPositive(false);
    setIsOpen(true);
    setStatus("idle");
  }

  function handleCancel() {
    resetFeedback();
  }

  async function handleSubmit() {
    if (positive === null) return;

    if (!sessionId) {
      toast?.error({
        title: "Unable to submit feedback",
        description: "Missing session information."
      });
      return;
    }

    setStatus("sending");

    try {
      await submitFeedback(sessionId, {
        positive,
        message: message.trim() || null,
        currentPin: lastEditedPin()?.id ?? null,
        currentPinState: lastEditedPin()?.state ?? null
      });
      setStatus("success");
    } catch {
      setStatus("error");
      toast?.error({
        title: "Feedback failed",
        description: "Something went wrong. Please try again."
      });
    }
  }

  useEffect(() => {
    if (status !== "success") return;

    const timeoutId = setTimeout(() => {
      resetFeedback();
    }, SUCCESS_COLLAPSE_DELAY_MS);

    // eslint-disable-next-line consistent-return
    return () => {
      clearTimeout(timeoutId);
    };
  }, [status]);

  return {
    positive,
    setPositive,
    message,
    setMessage,
    isOpen,
    setIsOpen,
    status,
    setStatus,
    handleSelectPositive,
    handleSelectNegative,
    handleCancel,
    handleSubmit,
    resetFeedback
  };
}
